import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Scale, FileSearch, PhoneCall, ShieldCheck, Gavel, BoxIcon } from 'lucide-react';
/**
 * Case activity / social-proof toast for Elias Navarro Law.
 * Shows recent consultation, case review, and client activity — no purchase /
 * dollar-amount claims (which could raise attorney-advertising concerns).
 */
const FIRST_NAMES = ['Sofia', 'Mateo', 'Camila', 'Lucas', 'Valentina', 'Diego', 'Isabella', 'Santiago', 'Lucia', 'Andres', 'Gabriela', 'Carlos', 'Elena', 'Marcus', 'Naomi', 'Jordan', 'Adrian', 'Priya', 'Ethan', 'Olivia', 'Liam', 'Emma', 'Noah', 'Ava', 'Mason', 'Mia', 'James', 'Charlotte', 'Benjamin', 'Amelia', 'Daniel', 'Harper', 'Alexander'];
const LAST_INITIALS = ['R.', 'D.', 'M.', 'G.', 'V.', 'L.', 'P.', 'C.', 'S.', 'T.', 'B.', 'N.', 'F.', 'O.', 'A.', 'H.'];
// Florida-weighted (firm's home state), with a few other Spanish-speaking US metros
const CITIES = ['Miami, FL', 'Miami, FL', 'Orlando, FL', 'Orlando, FL', 'Tampa, FL', 'Tampa, FL', 'Jacksonville, FL', 'Fort Lauderdale, FL', 'Hialeah, FL', 'St. Petersburg, FL', 'Tallahassee, FL', 'Cape Coral, FL', 'West Palm Beach, FL', 'Naples, FL', 'Gainesville, FL', 'Pensacola, FL', 'Atlanta, GA', 'Houston, TX', 'Dallas, TX', 'New York, NY'];
type EventType = 'consultation' | 'caseReview' | 'caseAccepted' | 'spokeWithElias' | 'protectedFromInsurance';
interface ActivityEvent {
  type: EventType;
  weight: number;
  icon: BoxIcon;
  /** Translation key for the action sentence — uses {practice} placeholder for some */
  copy: {
    en: string;
    es: string;
  };
  /** If true, append a practice-area tag */
  practiceArea?: boolean;
}
const PRACTICE_AREAS = {
  en: ['Car Accident', 'Truck Accident', 'Motorcycle Accident', 'Slip & Fall', 'Insurance Dispute', 'Wrongful Death'],
  es: ['Accidente de Auto', 'Accidente de Camión', 'Accidente de Motocicleta', 'Resbalón y Caída', 'Disputa de Seguros', 'Muerte Injusta']
};
const EVENTS: ActivityEvent[] = [{
  type: 'consultation',
  weight: 0.35,
  icon: PhoneCall,
  copy: {
    en: 'scheduled a free consultation',
    es: 'agendó una consulta gratuita'
  }
}, {
  type: 'caseReview',
  weight: 0.25,
  icon: FileSearch,
  practiceArea: true,
  copy: {
    en: 'requested a case review',
    es: 'solicitó una revisión de caso'
  }
}, {
  type: 'caseAccepted',
  weight: 0.18,
  icon: Scale,
  practiceArea: true,
  copy: {
    en: 'had their case accepted',
    es: 'tuvo su caso aceptado'
  }
}, {
  type: 'spokeWithElias',
  weight: 0.12,
  icon: Gavel,
  copy: {
    en: 'spoke directly with Elias',
    es: 'habló directamente con Elias'
  }
}, {
  type: 'protectedFromInsurance',
  weight: 0.1,
  icon: ShieldCheck,
  copy: {
    en: 'protected their claim from a low offer',
    es: 'protegió su reclamo de una oferta baja'
  }
}];
interface ToastEvent {
  id: number;
  fullName: string;
  city: string;
  event: ActivityEvent;
  practice?: string;
  timeAgo: string;
}
const FIRST_DELAY_MS = 3000;
const VISIBLE_MS = 5000;
const MIN_INTERVAL_MS = 9000;
const MAX_INTERVAL_MS = 16000;
const NAME_HISTORY_SIZE = 10;
function detectLang(): 'en' | 'es' {
  if (typeof document === 'undefined') return 'en';
  const stored = typeof window !== 'undefined' ? window.localStorage?.getItem('elias_navarro_lang') as 'en' | 'es' | null : null;
  if (stored === 'en' || stored === 'es') return stored;
  const html = document.documentElement.lang || '';
  return html.toLowerCase().startsWith('es') ? 'es' : 'en';
}
const COPY = {
  en: {
    just: 'just',
    from: 'from',
    label: 'Recent Activity'
  },
  es: {
    just: 'acaba de',
    from: 'desde',
    label: 'Actividad Reciente'
  }
};
function pickWeighted<T extends {
  weight: number;
}>(items: T[]): T {
  const total = items.reduce((s, i) => s + i.weight, 0);
  let r = Math.random() * total;
  for (const item of items) {
    r -= item.weight;
    if (r <= 0) return item;
  }
  return items[items.length - 1];
}
function pickWithHistory<T>(items: T[], history: T[]): T {
  const available = items.filter(i => !history.includes(i));
  const pool = available.length > 0 ? available : items;
  return pool[Math.floor(Math.random() * pool.length)];
}
function randomTimeAgo(lang: 'en' | 'es'): string {
  const minutes = Math.floor(Math.random() * 28) + 2; // 2–29 min
  if (lang === 'es') return `hace ${minutes} min`;
  return `${minutes} min ago`;
}
export function PurchaseToast() {
  const [active, setActive] = useState<ToastEvent | null>(null);
  const nameHistoryRef = useRef<string[]>([]);
  const idRef = useRef(0);
  const reduceMotion = useReducedMotion();
  const [lang, setLang] = useState<'en' | 'es'>('en');
  const copy = COPY[lang];
  useEffect(() => {
    setLang(detectLang());
    // Listen for language changes from the LanguageSwitcher (localStorage)
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'elias_navarro_lang') setLang(detectLang());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);
  useEffect(() => {
    let visibleTimer: number | undefined;
    let queueTimer: number | undefined;
    let cancelled = false;
    const showOne = () => {
      if (cancelled) return;
      const currentLang = detectLang();
      const firstName = pickWithHistory(FIRST_NAMES, nameHistoryRef.current);
      nameHistoryRef.current = [firstName, ...nameHistoryRef.current.slice(0, NAME_HISTORY_SIZE - 1)];
      const lastInitial = LAST_INITIALS[Math.floor(Math.random() * LAST_INITIALS.length)];
      const fullName = `${firstName} ${lastInitial}`;
      const city = CITIES[Math.floor(Math.random() * CITIES.length)];
      const event = pickWeighted(EVENTS);
      const practice = event.practiceArea ? PRACTICE_AREAS[currentLang][Math.floor(Math.random() * PRACTICE_AREAS[currentLang].length)] : undefined;
      idRef.current += 1;
      setActive({
        id: idRef.current,
        fullName,
        city,
        event,
        practice,
        timeAgo: randomTimeAgo(currentLang)
      });
      visibleTimer = window.setTimeout(() => {
        if (cancelled) return;
        setActive(null);
        const interval = MIN_INTERVAL_MS + Math.random() * (MAX_INTERVAL_MS - MIN_INTERVAL_MS);
        queueTimer = window.setTimeout(showOne, interval);
      }, VISIBLE_MS);
    };
    const firstTimer = window.setTimeout(showOne, FIRST_DELAY_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(firstTimer);
      if (visibleTimer) window.clearTimeout(visibleTimer);
      if (queueTimer) window.clearTimeout(queueTimer);
    };
  }, []);
  const motionProps = reduceMotion ? {
    initial: {
      opacity: 0
    },
    animate: {
      opacity: 1
    },
    exit: {
      opacity: 0
    },
    transition: {
      duration: 0.2
    }
  } : {
    initial: {
      opacity: 0,
      y: 24,
      scale: 0.96
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: 24,
      scale: 0.98
    },
    transition: {
      duration: 0.45,
      ease: [0.22, 1, 0.36, 1] as const
    }
  };
  return <div aria-live="polite" aria-atomic="true" className="fixed bottom-24 sm:bottom-28 left-1/2 -translate-x-1/2 z-[1000] pointer-events-none w-[calc(100%-2rem)] max-w-md flex justify-center" data-id="element-233">
      <AnimatePresence mode="wait" data-id="element-234">
        {active && <motion.div key={active.id} {...motionProps} className="pointer-events-auto flex items-stretch gap-0 bg-[#0d0d0d]/95 backdrop-blur-xl border border-glass-border rounded-sm shadow-[0_20px_60px_rgba(0,0,0,0.6)] overflow-hidden min-w-[300px]" role="status" data-id="element-235">
            {/* Crimson accent bar */}
            <div className="w-[3px] bg-crimson flex-shrink-0" data-id="element-236" />

            {/* Icon */}
            <div className="flex-shrink-0 flex items-center justify-center px-4 bg-gradient-to-br from-crimson/15 to-transparent border-r border-glass-border" data-id="element-237">
              <div className="relative" data-id="element-238">
                <active.event.icon className="w-5 h-5 text-crimson-glow" strokeWidth={1.5} data-id="element-239" />
                <span className="absolute -top-1 -right-1 w-2 h-2 bg-crimson-glow rounded-full animate-pulse ring-2 ring-[#0d0d0d]" data-id="element-240" />
              </div>
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0 py-3 pr-4 pl-4" data-id="element-241">
              <div className="flex items-center justify-between gap-2 mb-1" data-id="element-242">
                <span className="text-[8px] tracking-[3px] uppercase text-gold-light font-light" data-id="element-243">
                  {copy.label}
                </span>
                <span className="text-[9px] text-[#f5f5f5]/30 tabular-nums" data-id="element-244">
                  {active.timeAgo}
                </span>
              </div>
              <p className="text-[12px] leading-[1.45] text-white font-medium tracking-[0.2px]" data-id="element-245">
                <span className="font-semibold" data-id="element-246">{active.fullName}</span>{' '}
                <span className="text-[#f5f5f5]/45 font-light" data-id="element-247">
                  {copy.from} {active.city}
                </span>
              </p>
              <p className="text-[11px] leading-[1.45] text-[#f5f5f5]/65 font-light mt-0.5" data-id="element-248">
                {copy.just}{' '}
                <span className="text-white/85" data-id="element-249">{active.event.copy[lang]}</span>
                {active.practice && <>
                    {' '}
                    <span className="text-crimson-glow font-medium" data-id="element-250">
                      · {active.practice}
                    </span>
                  </>}
              </p>
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}